import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Splits = () => {
  const [data, setData] = useState({ collaborators: [], history: [] });
  const [form, setForm] = useState({ name: "", email: "", role: "Collaborator", percent: 10 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/splits");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const totalPct = data.collaborators.reduce((s, c) => s + Number(c.percent), 0);

  const add = async () => {
    if (!form.name || !form.email || !form.percent) {
      toast.error("Fill all fields");
      return;
    }
    setBusy(true);
    try {
      await api.post("/splits", { ...form, percent: Number(form.percent) });
      toast.success("Collaborator added");
      setForm({ name: "", email: "", role: "Collaborator", percent: 10 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/splits/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="splits-page">
      <PageHeader
        overline="Collaboration"
        title="Revenue splits"
        description="Auto-distribute invoice income to your team by percentage. Updates apply to future payments."
      />

      <div className="p-4 md:p-8 grid md:grid-cols-12 gap-8">
        <section className="md:col-span-7 border border-[#2c241e]">
          <div className="p-6 border-b border-[#2c241e] flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Collaborators</div>
            <div className={`text-[10px] uppercase tracking-[0.22em] font-mono ${totalPct > 100 ? "text-rose-400" : totalPct === 100 ? "text-emerald-400" : "text-zinc-400"}`} data-testid="total-percent">
              {totalPct.toFixed(1)}% allocated
            </div>
          </div>
          {data.collaborators.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 text-sm" data-testid="splits-empty">
              No collaborators yet. Add one to enable auto-splits on incoming payments.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#2c241e]">
                  <th className="p-4 font-normal">Name</th>
                  <th className="p-4 font-normal">Role</th>
                  <th className="p-4 font-normal text-right">Share</th>
                  <th className="p-4 font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {data.collaborators.map((c) => (
                  <tr key={c.split_id} className="border-b border-[#2c241e] last:border-b-0" data-testid={`collab-row-${c.name}`}>
                    <td className="p-4">
                      <div className="text-sm">{c.name}</div>
                      <div className="text-xs text-zinc-500">{c.email}</div>
                    </td>
                    <td className="p-4 text-sm text-zinc-300">{c.role}</td>
                    <td className="p-4 text-right font-mono text-sm">{Number(c.percent).toFixed(1)}%</td>
                    <td className="p-4 text-right">
                      <button onClick={() => del(c.split_id)} data-testid={`delete-collab-${c.name}`} className="text-zinc-500 hover:text-rose-400">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="md:col-span-5 border border-[#2c241e] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-5">Add collaborator</div>
          <div className="space-y-4">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="collab-name-input"
              className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white" />
            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="collab-email-input"
              className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white" />
            <input placeholder="Role (e.g. Editor)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="collab-role-input"
              className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2 flex justify-between">
                <span>Share</span>
                <span className="font-mono">{Number(form.percent || 0).toFixed(1)}%</span>
              </div>
              <input type="range" min="0" max="100" step="0.5" value={form.percent}
                onChange={(e) => setForm({ ...form, percent: Number(e.target.value) })}
                data-testid="collab-percent-slider"
                className="w-full accent-white" />
            </div>
            <button onClick={add} disabled={busy} data-testid="add-collab-btn"
              className="w-full bg-white text-black text-sm font-medium px-4 py-2.5 rounded-md hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <Plus size={14} strokeWidth={2.5} /> Add collaborator
            </button>
          </div>
        </section>
      </div>

      {/* History */}
      <div className="p-4 md:p-8 pt-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">Split payout history</div>
        <div className="border border-[#2c241e]">
          {data.history.length === 0 ? (
            <div className="p-4 md:p-8 text-center text-zinc-500 text-sm" data-testid="splits-history-empty">
              No splits have been distributed yet. Once an invoice is paid, splits will appear here.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#2c241e]">
                  <th className="p-4 font-normal">Date</th>
                  <th className="p-4 font-normal">Collaborator</th>
                  <th className="p-4 font-normal">Invoice</th>
                  <th className="p-4 font-normal text-right">Share</th>
                  <th className="p-4 font-normal text-right">Amount</th>
                  <th className="p-4 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.split_payout_id} className="border-b border-[#2c241e] last:border-b-0">
                    <td className="p-4 text-sm text-zinc-400">{fmtDate(h.created_at)}</td>
                    <td className="p-4 text-sm">{h.collaborator_name}</td>
                    <td className="p-4 font-mono text-xs text-zinc-400">{h.invoice_id}</td>
                    <td className="p-4 text-right font-mono text-sm">{Number(h.percent).toFixed(1)}%</td>
                    <td className="p-4 text-right font-mono text-sm">{fmtMoney(h.amount, h.currency)}</td>
                    <td className="p-4 text-xs uppercase tracking-[0.18em] text-amber-400">{h.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Splits;
